/**
 *
 * Assessment/AssessmentSurvey
 *
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import {
  makeSelectAuth,
  makeSelectProfile,
  makeSelectAnonymousId,
} from 'containers/Auth/selectors';
import { setAICCRedirected } from 'containers/Main/actions';
import {
  makeSelectAICCUser,
  makeSelectAICCRedirected,
  makeSelectClientDetails,
  makeSelectClinicMode,
} from 'containers/Main/selectors';
import Mixpanel from 'utils/mixpanelService';
import Grid from '@material-ui/core/Grid';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isNull from 'lodash/isNull';
import { useFirestore } from 'react-redux-firebase';
import _cloneDeep from 'lodash/cloneDeep';
import Survey from 'components/Survey';
import Disclaimer from 'components/ResourceAndAssessmentDisclaimer';
import { getClientDemographicsQuestions } from 'components/Survey/utils';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { getLocalData, setLocalData } from 'utils/localDataStore';
import {
  generateRandomChars,
  getImageFile,
  getParsedUserAgeInfo,
} from 'utils/stringUtils';
import { putAssessment } from './actions';
import {
  convertToPattern,
  getAssessmentExcludedQuestions,
  getAssessmentJSON,
  getCleanResult,
  getDataFromResult,
} from './utils';
import IntroNote from './components/IntroNote';
import PersonQuote from './components/PersonQuote';
import ResumeOverlay from './components/ResumeOverlay';

const useStyles = makeStyles({
  root: {
    marginTop: -64,
    zIndex: 99,
    padding: '0 !important',
  },
  surveyWrapper: {
    position: 'relative',
    background: '#fff',
    borderRadius: 3,
  },
});

const stateSelector = createStructuredSelector({
  aiccUser: makeSelectAICCUser(),
  aiccRedirected: makeSelectAICCRedirected(),
  auth: makeSelectAuth(),
  clientDetails: makeSelectClientDetails(),
  profile: makeSelectProfile(),
  sessionAnonymousId: makeSelectAnonymousId(),
  clinicMode: makeSelectClinicMode(),
});

const AssessmentSurvey = ({
  theme,
  assessmentPath,
  assessment,
  trackingProps,
  spanFullWidth,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const firestore = useFirestore();
  const history = useHistory();
  const startTimestamp = useRef();
  const [
    insuranceSiteConfig,
    ageGenderMappingSiteConfig,
  ] = useSiteConfigSelector(['Insurance', 'Age and Gender Mappings']);

  const {
    aiccUser,
    aiccRedirected,
    auth,
    clientDetails,
    profile,
    sessionAnonymousId,
    clinicMode,
  } = useSelector(stateSelector);

  const [temporaryAnswers, setTemporaryAnswers] = useState({
    data: null,
    isLoaded: false,
  });
  const [shouldResume, setShouldResume] = useState(
    clinicMode ? false : undefined,
  );

  const muiTheme = useTheme();
  const isXs = useMediaQuery(muiTheme.breakpoints.down('xs'));

  const isAICC = _get(clientDetails, 'aicc', false);
  const userIdTemp = getLocalData('userIdTemp');
  let userId;
  if (isAICC) {
    userId = _get(aiccUser, 'user_id');
  } else if (auth.isLoaded) {
    if (auth.isEmpty) {
      userId = clinicMode
        ? sessionAnonymousId || userIdTemp
        : userIdTemp || sessionAnonymousId;
    } else {
      userId = auth.uid;
    }
  }

  useEffect(() => {
    if (userId) {
      firestore
        .collection('assessment_temporary_answers')
        .doc(userId)
        .get()
        .then(
          snapshot => {
            const data = snapshot.data();
            setTemporaryAnswers({
              data: data || null,
              isLoaded: true,
            });
            if (_isEmpty(_get(data, [slug, 'answers'], {})))
              setShouldResume(false);
            else setShouldResume(null);
          },
          () => {
            setTemporaryAnswers({
              data: null,
              isLoaded: true,
            });
            setShouldResume(false);
          },
        );
    } else if (_isNull(userId)) {
      setTemporaryAnswers({
        data: null,
        isLoaded: true,
      });
      setShouldResume(false);
    }
  }, [firestore, slug, userId]);

  useEffect(() => {
    if (aiccUser.lesson_location && !aiccRedirected) {
      dispatch(setAICCRedirected(true));
      history.replace(`/assessments/${slug}/results`);
    }
  }, [aiccUser]);

  useEffect(() => {
    if (!_isEmpty(assessment) && auth.isLoaded) {
      startTimestamp.current = Date.now();
    }
  }, [assessment, auth]);

  const onStart = () => {
    const {
      fields: { name },
      sys: { id },
    } = assessment.items[0];

    Mixpanel.track('Started Assessment', {
      name,
      type: 'assessment',
      entryId: id,
      ...trackingProps,
    });
  };

  const handleResumeOverlay = value => setShouldResume(value);

  const onComplete = (answers, type, shouldSendEmail) => {
    const result = _cloneDeep(answers);

    const customDemographicsQuestions = getClientDemographicsQuestions(
      clientDetails,
    );

    // TODO: Remove when month/year approach spreads
    const dob =
      getDataFromResult(
        result,
        'ageHelpAtHand',
        _get(customDemographicsQuestions, 'ageHelpAtHand'),
      ) ||
      getDataFromResult(
        result,
        'ageRangeStandard',
        _get(customDemographicsQuestions, 'ageRangeStandard'),
      ) ||
      profile.dob ||
      null;
    const birthdayMonthYear =
      getDataFromResult(result, 'ageMonthYearStandard') ||
      profile.birthdayMonthYear ||
      null;
    const gender =
      getDataFromResult(
        result,
        'genderHelpAtHand',
        _get(customDemographicsQuestions, 'genderHelpAtHand'),
      ) ||
      getDataFromResult(
        result,
        'genderStandard',
        _get(customDemographicsQuestions, 'genderStandard'),
      ) ||
      profile.gender ||
      null;
    const genderOther =
      getDataFromResult(result, 'other-option/genderStandard') ||
      profile.genderOther ||
      null;
    const genderLGBTQp =
      getDataFromResult(
        result,
        'genderLGBTQStandard',
        _get(customDemographicsQuestions, 'genderLGBTQStandard'),
      ) ||
      profile.genderLGBTQp ||
      null;
    const industry =
      getDataFromResult(
        result,
        'industryStandard',
        _get(customDemographicsQuestions, 'industryStandard'),
      ) ||
      profile.industry ||
      null;
    const insurance =
      getDataFromResult(result, 'healthInsuranceStatus') ||
      profile.insurance ||
      null;
    const cleanResult = getCleanResult(result);
    const data = assessment.items[0];
    const { callToAction, name, time, slug } = data.fields;

    const randomIdentifier = generateRandomChars(36);

    const payload = {
      isAnonymous: type === 'onlyResults',
      dob: convertToPattern(
        'dob',
        dob,
        ageGenderMappingSiteConfig,
        clientDetails.shortName,
      ),
      birthdayMonthYear,
      gender: convertToPattern('gender', gender, ageGenderMappingSiteConfig),
      genderOther,
      genderLGBTQp,
      randomIdentifier,
      industry: convertToPattern(
        'industry',
        industry,
        ageGenderMappingSiteConfig,
      ),
      insurance: convertToPattern(
        'insurance',
        insurance,
        insuranceSiteConfig,
        clientDetails.shortName,
      ),
      assessment: {
        assessmentID: data.sys.id,
        callToAction: callToAction || null,
        name: name || null,
        time: time || null,
        slug: slug || null,
      },
      startTimestamp: startTimestamp.current,
      timestamp: Date.now(),
      resumed: Boolean(shouldResume),
      result: cleanResult,
      cobrand: getLocalData('brand') || 'none',
      sendEmail: shouldSendEmail,
      assessmentPath,
    };
    if (type === 'onlyResults') {
      setLocalData('assessmentDemographics', {
        id: clinicMode
          ? sessionAnonymousId || userIdTemp
          : userIdTemp || sessionAnonymousId,
        dob: payload.dob,
        birthdayMonthYear,
        gender,
        genderOther,
        genderLGBTQp,
        industry: payload.industry,
        insurance: payload.insurance,
      });
    }

    Mixpanel.track('Completed Assessment', {
      name,
      type: 'assessment',
      ...getParsedUserAgeInfo(birthdayMonthYear),
      entryId: data.sys.id,
      randomIdentifier,
      ...trackingProps,
    });

    dispatch(putAssessment(payload));
  };

  if (_isEmpty(assessment) || !auth.isLoaded) return null;
  const {
    fields: { assessment: assessmentObject, name, factoid, source, slug },
    sys: { id },
  } = assessment.items[0];
  const hasSource = !_isEmpty(source);
  const hasQuote = !_isEmpty(factoid);
  const quote = hasQuote && _get(factoid, 'fields.article');
  const quoteAuthor = hasQuote && _get(factoid, 'fields.person.0.fields.name');
  const quoteTitle = hasQuote && _get(factoid, 'fields.person.0.fields.title');
  const quoteImage =
    hasQuote && getImageFile(_get(factoid, 'fields.person.0..fields'));
  const profileFields = _get(clientDetails, 'metadata.userProfileFields');
  const excludedQuestions = getAssessmentExcludedQuestions(
    profile,
    profileFields,
    ageGenderMappingSiteConfig,
  );

  const model = getAssessmentJSON(
    assessmentObject,
    null,
    false,
    profile,
    _get(insuranceSiteConfig, 'config.assessmentQuestionNames'),
    excludedQuestions,
  );

  return (
    <Grid
      container
      direction="row"
      spacing={isXs ? 0 : 2}
      className={classes.root}
    >
      <Grid item xs={12} md={spanFullWidth ? 12 : 9}>
        <Grid container>
          <Grid
            item
            xs={12}
            className={classes.surveyWrapper}
            id="survey-wrapper"
          >
            <ResumeOverlay
              visible={temporaryAnswers.isLoaded && shouldResume === null}
              onClick={handleResumeOverlay}
            />
            <Survey
              theme={theme}
              model={model}
              onComplete={onComplete}
              onStart={onStart}
              isSignedIn={!auth.isEmpty}
              slug={slug}
              assessmentName={name}
              id={id}
              temporaryAnswers={temporaryAnswers.data}
              isTemporaryAnswersLoaded={temporaryAnswers.isLoaded}
              shouldResume={shouldResume}
              assessmentPath={assessmentPath}
            />
          </Grid>
          <Grid item xs={12}>
            <Disclaimer
              type="assessment-disclaimer"
              page="assessments"
              justifyText
            />
          </Grid>
        </Grid>
      </Grid>
      {hasQuote && (
        <Grid item xs={12} md={3}>
          <PersonQuote
            quote={quote}
            author={quoteAuthor}
            title={quoteTitle}
            image={quoteImage}
          />
          {hasSource && <IntroNote data={source} />}
        </Grid>
      )}{' '}
    </Grid>
  );
};

AssessmentSurvey.propTypes = {
  theme: PropTypes.object,
};

export default AssessmentSurvey;
